/**
 * SSE 事件合帧派发器(性能债修复 · 热点 1-4 降频)。
 *
 * <p>流式期每个 token 都会触发一轮"状态变更 → 渲染",而渲染成本随会话体量增长
 * (时间线分组、Markdown 重解析、虚拟树 diff)。合帧器把高频事件(文本增量、
 * 工具事件)缓冲约一帧窗口,窗口内按原顺序一次性应用——状态语义逐字节不变
 * (仍是顺序喂给 {@link dispatchChatStreamEvent}),仅渲染频率与事件频率解耦。</p>
 *
 * <p>用户交互与终态事件(提问卡、确认卡、完成、错误)立即冲刷,零感知延迟;
 * 冲刷时连同之前缓冲的事件一并按序应用,保证交互事件之前的事件全部先行。</p>
 */
import type { ChatStreamBridge } from './streamEvents'
import { dispatchChatStreamEvent } from './streamEvents'

/** 合帧窗口:约一帧多一点,既压频率又保持流式观感。 */
export const STREAM_COALESCE_WINDOW_MS = 64

/** 立即冲刷的事件:用户交互与终态,不允许被缓冲延迟。 */
const IMMEDIATE_EVENTS = new Set([
  'pending_question',
  'pending_confirmation',
  'pending_resolved',
  'run_done',
  'run_error'
])

export interface CoalescedDispatcher {
  /** 替代原 onEvent:事件进入缓冲或立即冲刷。 */
  onEvent: (eventName: string, data: unknown) => void
  /** 流结束/出错/切换时同步冲刷残余缓冲;幂等。 */
  flush: () => void
}

/**
 * 创建合帧派发器。
 *
 * @param apply 事件应用函数（现状形态：逐事件构建 bridge 后调 dispatchChatStreamEvent）。
 *   合帧器不改变其语义，只把调用时机从“每事件一拍”改为“每帧一批”。
 */
export function createCoalescedDispatcher(
  apply: (eventName: string, data: unknown) => void,
  windowMs: number = STREAM_COALESCE_WINDOW_MS
): CoalescedDispatcher {
  let buffer: Array<{ eventName: string; data: unknown }> = []
  let timer: ReturnType<typeof setTimeout> | null = null

  function flush(): void {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (buffer.length === 0) {
      return
    }
    const batch = buffer
    buffer = []
    for (const item of batch) {
      apply(item.eventName, item.data)
    }
  }

  function onEvent(eventName: string, data: unknown): void {
    buffer.push({ eventName, data })
    if (IMMEDIATE_EVENTS.has(eventName)) {
      // 连同之前缓冲的事件一并按序冲刷,保证交互/终态事件前的事件全部先行。
      flush()
      return
    }
    if (timer === null) {
      timer = setTimeout(flush, windowMs)
    }
  }

  return { onEvent, flush }
}

/** 便捷入口：直接从静态 bridge 创建合帧派发器。 */
export function createCoalescedDispatcherForBridge(
  bridge: ChatStreamBridge,
  windowMs?: number
): CoalescedDispatcher {
  return createCoalescedDispatcher(
    (eventName, data) => dispatchChatStreamEvent(eventName, data, bridge),
    windowMs
  )
}
